import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { FlaskConical, BadgeCheck, Crown, Loader2, MessageSquare } from "lucide-react";
import { API } from "@/lib/api";

export default function BetaStatus() {
  const [st, setSt] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/users/beta-status`)
      .then((r) => setSt(r.data))
      .catch(() => setSt(null))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="p-8 text-white/50 flex items-center gap-2"><Loader2 size={16} className="animate-spin" /> Chargement…</div>;

  const offer = st?.lifetime_offer || {};
  return (
    <div className="p-6 max-w-3xl space-y-5" data-testid="beta-status-page">
      <div>
        <div className="flex items-center gap-3">
          <FlaskConical size={24} className="text-cyan-400" />
          <h1 className="font-heading font-bold text-2xl tracking-tight">Programme Beta</h1>
          {st?.is_beta_tester && (
            <span className="inline-flex items-center gap-1.5 rounded-full border border-cyan-500/50 bg-cyan-500/10 px-3 py-1 text-[11px] font-semibold tracking-wide text-cyan-200" data-testid="beta-badge-page">
              <span className="h-1.5 w-1.5 rounded-full bg-cyan-400" /> BETA TESTEUR
            </span>
          )}
        </div>
        <p className="text-white/50 text-sm mt-1">Votre statut dans le programme de test ANATRADEX.</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div className="panel p-4">
          <div className="text-[10px] uppercase tracking-widest text-white/40">Statut</div>
          <div className="mt-1 font-heading text-lg font-semibold" data-testid="beta-status-value">
            {st?.is_beta_tester ? "Testeur approuvé" : st?.application_status === "pending" ? "Candidature en attente" : "Non testeur"}
          </div>
        </div>
        <div className="panel p-4">
          <div className="text-[10px] uppercase tracking-widest text-white/40">Accès</div>
          <div className="mt-1 font-heading text-lg font-semibold text-cyan-300">
            {st?.is_beta_tester ? "Accès bêta complet" : "Standard"}
          </div>
        </div>
        <div className="panel p-4">
          <div className="text-[10px] uppercase tracking-widest text-white/40">Depuis</div>
          <div className="mt-1 font-heading text-lg font-semibold">
            {st?.beta_tester_since ? new Date(st.beta_tester_since).toLocaleDateString("fr-FR") : "—"}
          </div>
        </div>
      </div>

      {st?.is_beta_tester && (
        <div className="panel p-5 border border-cyan-500/30" data-testid="beta-access-panel">
          <div className="flex items-center gap-2 text-cyan-200 font-semibold"><BadgeCheck size={16} /> Accès bêta complet actif</div>
          <p className="text-sm text-white/60 mt-2">
            Votre compte est <b className="text-white">exempté de la rétrogradation</b> en fin d&apos;essai pendant toute la phase beta :
            vous conservez l&apos;accès complet aux fonctionnalités Expert tant que le programme est ouvert.
            En échange, vos retours comptent — le bouton <b className="text-white">« Feedback beta »</b> dans la barre latérale nous parvient directement.
          </p>
        </div>
      )}

      <div className="panel p-5" data-testid="beta-offer-panel">
        <div className="flex items-center gap-2 font-heading text-lg font-semibold"><Crown size={18} className="text-warn" /> Offre Founder — Expert à vie pour 199 €</div>
        <p className="text-sm text-white/60 mt-2">
          Réservée aux premiers soutiens : un paiement unique de 199 € donne l&apos;accès Expert <b className="text-white">à vie</b> (aucun abonnement).
          {" "}<span className="text-white/80">{offer.seats_taken} / {offer.seats_total} places prises.</span>
        </p>
        {offer.active ? (
          <div className="mt-4 rounded-lg border border-up/30 bg-up/10 p-3 text-sm text-up" data-testid="beta-offer-active">✓ Votre accès Expert à vie est actif. Merci pour votre soutien !</div>
        ) : offer.eligible ? (
          <Link to="/pricing" className="btn btn-primary text-sm mt-4 inline-flex" data-testid="beta-offer-cta">Obtenir l&apos;offre Founder 199 €</Link>
        ) : (
          <div className="mt-4 rounded-lg border border-white/10 bg-white/[0.03] p-3 text-sm text-white/50" data-testid="beta-offer-unavailable">
            {st?.is_beta_tester ? "Offre non disponible pour ce compte (abonnement actif ou quota atteint)." : "Offre réservée aux testeurs beta approuvés."}
          </div>
        )}
      </div>

      {st?.is_beta_tester && (
        <div className="flex items-start gap-2.5 rounded-lg border border-accent-blue/25 bg-accent-blue/8 px-3.5 py-2.5 text-xs text-white/70">
          <MessageSquare size={13} className="text-accent-blue shrink-0 mt-0.5" />
          <span>Votre mission : importer vos trades → générer votre rapport statistique → nous dire ce qui cloche. La checklist du tableau de bord suit votre progression.</span>
        </div>
      )}
    </div>
  );
}
